import { Fragment, useEffect, useMemo, useRef } from '@wordpress/element'
import { applyFilters } from '@wordpress/hooks'
import { __ } from '@wordpress/i18n'
import { useQuery } from 'react-query'
import { ExtraServicesContent, ServiceOptionItem, ServiceOptionsBox, ServiceOptionsGroup } from './ExtraServices'
import styled from '@emotion/styled'

const { BlockTitle, ModalSection, SkeletonCategories } = window.wptravelengine?.activityComponents || {};

const SelectExtraServices = ({ state, setState, data, isFetching: _isFetching, dates, packages, ...rest }) => {
    const { summary: { extraServices, selectedPackage, selectedDate, travelers } } = state
    const totalTravelers = travelers?.reduce((total, traveler) => total + traveler.qty, 0)
    const { tripID } = data
    const { data: rawServices = [], isLoading: isServiceLoading } = useQuery(['extraservices', tripID, selectedDate], () => fetch(`${wpApiSettings.root}wptravelengine/v2/trips/${tripID}/services${selectedDate ? `?date=${selectedDate}` : ''}`).then(r => r.json()))
    const isFetching = _isFetching || isServiceLoading

    const services = useMemo(
        () => (Array.isArray(rawServices) ? rawServices : []).map((service, index) =>
            applyFilters('wptravelengine.extraServices.service', service, { index })
        ),
        [rawServices]
    )

    const updateSummary = (newSummary) => {
        setState(prev => ({
            ...prev,
            summary: {
                ...prev.summary,
                ...newSummary
            }
        }))
    }

    useEffect(() => {
        if (extraServices.length === 0 && selectedDate) {
            const extraServices = services.flatMap(({ id, required, options, multiple, type }) => {
                const mapOption = option => ({
                    service_id: id,
                    id: option.key,
                    qty: 0,
                    label: option.label,
                    title: option.title,
                    unitPrice: option.price,
                    perTextLabel: ` / ${option.serviceUnit.value}`,
                    perTextDescription: option.description,
                    total: 0,
                    required
                });

                const isSoldOut = option => Number.isInteger(option?.avaQty) && option.avaQty === 0
                const defaultOption = options.find(option => !isSoldOut(option)) || options[0]
                const singleSelectable = !multiple && type === 'custom'

                if (multiple) return options.map(mapOption)
                if (!options.length) return []
                // Choose-one groups start with nothing selected (id: null).
                if (singleSelectable) return [{ ...mapOption(defaultOption), id: null }]
                return [mapOption(defaultOption)]
            });
            updateSummary({ extraServices });
        }
    }, [services, selectedDate])

    // Core's own initial population (App.jsx) always defaults a non-multiple service to its
    // first option regardless of stock, and runs before this component's own populate effect
    // above ever gets a chance (extraServices is no longer empty by then). If the current
    // selection is sold out for THIS date but a different option isn't, switch to it — but only
    // while qty is still 0, so we never override a quantity the user has actually set.
    useEffect(() => {
        if (!selectedDate || !services.length) return

        const isSoldOut = option => Number.isInteger(option?.avaQty) && option.avaQty === 0
        let changed = false

        const corrected = extraServices.map(es => {
            if (es.qty !== 0) return es
            const service = services.find(s => s.id === es.service_id)
            if (!service || service.multiple) return es
            const currentOption = service.options.find(o => o.key === es.id)
            if (!currentOption || !isSoldOut(currentOption)) return es
            const betterOption = service.options.find(o => !isSoldOut(o))
            if (!betterOption || betterOption.key === es.id) return es

            changed = true
            return {
                ...es,
                id: betterOption.key,
                label: betterOption.label,
                title: betterOption.title,
                unitPrice: betterOption.price,
                perTextLabel: ` / ${betterOption.serviceUnit.value}`,
                perTextDescription: betterOption.description,
            }
        })

        if (changed) updateSummary({ extraServices: corrected })
    }, [services, extraServices, selectedDate])

    // Choose-one (single-selectable) groups must start with NOTHING selected. Core's initial
    // population (App.jsx) defaults every non-multiple service to its first option, and our own
    // populate effect can't override it once extraServices is non-empty. Clear the pre-selection
    // for each choose-one service exactly once (tracked by ref) so the group renders empty until
    // the user explicitly picks an option — never touching a selection the user has since made.
    const clearedChooseOne = useRef(new Set())
    useEffect(() => {
        if (!selectedDate || !services.length || !extraServices.length) return

        let changed = false
        const corrected = extraServices.map(es => {
            const service = services.find(s => s.id === es.service_id)
            if (!service || service.multiple || service.type !== 'custom') return es
            if (clearedChooseOne.current.has(service.id)) return es
            clearedChooseOne.current.add(service.id)
            if (es.id === null) return es
            changed = true
            return { ...es, id: null, qty: 0, total: 0 }
        })

        if (changed) updateSummary({ extraServices: corrected })
    }, [services, extraServices, selectedDate])

    // proceed to checkout
    // Core's own check (App.jsx) intentionally skips required-service validation when this addon
    // is active (it defers to us via the expectedVersion gate) and doesn't watch extraServices at
    // all in that case. So instead of ANDing against core's last value (which can only ever
    // downgrade it and can never re-raise it once a required service is later fulfilled), we
    // independently recompute the full formula here using our own date-aware services.
    useEffect(() => {
        const requiredServiceIds = services?.filter(service => service.required)?.map(service => service.id) || []
        const allRequiredServicesSelected = requiredServiceIds.every(serviceId => {
            const serviceOptions = extraServices.filter(service => service.service_id === serviceId)
            return serviceOptions.some(service => service.qty > 0)
        })

        const selectedTripPackage = packages?.find(({ id }) => id === selectedPackage)
        const minPax = selectedTripPackage?.min_pax || 1
        const requiredTravelers = totalTravelers >= minPax
        const packageDates = dates?.find(d => d.package.id === selectedPackage)?.dates
        const availableTimes = packageDates?.[selectedDate]?.times?.filter(time => (time.seats === '' || time.seats >= totalTravelers))

        const corrected = !!(requiredTravelers && allRequiredServicesSelected && selectedDate &&
            (!availableTimes?.length || state.summary.selectedTime) && totalTravelers > 0)

        setState(prev => prev.proceedToCheckout === corrected ? prev : { ...prev, proceedToCheckout: corrected })
    }, [services, extraServices, selectedPackage, selectedDate, totalTravelers, dates, packages, state.summary.selectedTime])

    const handleUpdateQuantity = (id, qty) => {
        updateSummary({
            extraServices: extraServices?.map(service => {
                if (service.id === id) {
                    const total = service?.unitPrice * qty
                    return { ...service, qty, total }
                }
                return service
            })
        })
    }

    const handleUpdateKey = (id, key) => {
        updateSummary({
            extraServices: extraServices.map(service => {
                if (service.service_id === id) {
                    const newOption = services.find(s => s.id === id).options.find(o => o.key === key)
                    return {
                        ...service,
                        id: key,
                        unitPrice: newOption.price,
                        title: newOption.title,
                        qty: 1,
                        total: newOption.price,
                    }
                }
                return service
            })
        })
    }

    // Deselect a single-selectable (choose-one) service: null out the chosen option
    // key and reset quantity, so the group renders with nothing selected — matching the
    // toggle-off behavior of the trip booking modal's ExtraServices choose-one group.
    const handleDeselect = (id) => {
        updateSummary({
            extraServices: extraServices.map(service =>
                service.service_id === id ? { ...service, id: null, qty: 0, total: 0 } : service
            )
        })
    }

    const allSoldOut = Array.isArray(services) && services.length > 0 &&
        services.every(service => service.options?.every(opt => Number.isInteger(opt?.avaQty) && opt.avaQty === 0))

    if (!selectedPackage || !selectedDate || extraServices.length === 0 || totalTravelers === 0) return null;
    const showSoldOutServices = applyFilters('wptravelengine.extraServices.showAllSoldOut', !!(window.wpteRM?.settings?.extraServices?.showSoldOutService), { services })
    if (allSoldOut && !showSoldOutServices) return null;

    const filteredServices = services?.filter(service => applyFilters('wptravelengine.extraServices.renderService', true, { service }))

    const isOptSoldOut = (opt) => Number.isInteger(opt?.avaQty) && opt.avaQty === 0
    const getMax = (opt) => Number.isInteger(opt?.avaQty) ? opt.avaQty : Infinity

    const renderOption = (option, { label, value, onChange, selectable = false, selected = false, onSelect, required: itemRequired = false, min = 0 }) => {
        const perTextLabel = option?.serviceUnit ? `/ ${option.serviceUnit.label}` : ''
        const defaultItem = <ServiceOptionItem
            key={option.key}
            label={label ?? (option.title || option.label)}
            image={false}
            description={option.description}
            perTextLabel={perTextLabel}
            price={option.price}
            isSoldOut={isOptSoldOut(option)}
            required={itemRequired}
            selectable={selectable}
            selected={selected}
            onSelect={onSelect}
            min={min}
            max={getMax(option)}
            value={value}
            onChange={onChange}
        />
        return applyFilters(
            'wptravelengine.extraServices.activityOptionItem',
            defaultItem,
            { option, label: label ?? (option.title || option.label), price: option.price, perTextLabel, perTextDescription: option.description, required: itemRequired }
        )
    }

    return (
        <>
            {filteredServices?.length > 0 && <>
                <BlockTitle style={{ minHeight: 34 }}>{!isFetching && (wteL10n?.l10n?.extraServicesTitle || __("Extra Services", "wte-extra-services"))}</BlockTitle>
                <ModalSection>
                    {isFetching ? <SkeletonCategories /> : <ActivityExtraServicesContent>
                        {filteredServices.map(({ id, required, title, options, multiple, type, badgeLabel }) => {
                            const _service = extraServices.filter(service => service.service_id === id)
                            const singleSelectable = !multiple && type === 'custom'
                            const grouped = multiple || type === 'custom'

                            let items
                            if (multiple) {
                                items = _service.map(({ id: _id, qty }) => {
                                    const option = options.find(o => o.key === _id)
                                    if (!option) return null
                                    return renderOption(option, {
                                        value: qty,
                                        onChange: count => handleUpdateQuantity(_id, count),
                                    })
                                })
                            } else if (singleSelectable) {
                                const currentKey = _service[0]?.id
                                items = options.map(option => {
                                    const selected = option.key === currentKey
                                    return renderOption(option, {
                                        min: 1,
                                        selectable: true,
                                        selected,
                                        onSelect: () => {
                                            if (isOptSoldOut(option)) return
                                            if (selected) {
                                                handleDeselect(id)
                                                return
                                            }
                                            handleUpdateKey(id, option.key)
                                        },
                                        value: selected ? (_service.find(s => s.id === option.key)?.qty ?? 0) : 0,
                                        onChange: count => handleUpdateQuantity(option.key, count),
                                    })
                                })
                            } else {
                                const entry = _service[0]
                                const option = entry ? options.find(o => o.key === entry.id) : options[0]
                                const displayLabel = options.length === 1 && options[0]
                                    ? (options[0]?.title || options[0]?.label)
                                    : (option?.title || title)
                                items = option ? renderOption(option, {
                                    label: displayLabel,
                                    value: entry?.qty ?? 0,
                                    onChange: count => handleUpdateQuantity(option.key, count),
                                    required,
                                }) : null
                            }

                            return grouped ? (
                                <ServiceOptionsGroup
                                    key={id}
                                    title={title}
                                    required={required}
                                    chooseOne={singleSelectable}
                                    badgeLabel={badgeLabel}
                                    Box={ActivityServiceOptionBox}
                                    bodyClassName="wte-service-multiple-options-body">
                                    {items}
                                </ServiceOptionsGroup>
                            ) : (
                                <Fragment key={id}>{items}</Fragment>
                            )
                        })}
                    </ActivityExtraServicesContent>}
                </ModalSection>
            </>}
        </>
    )
}

export default SelectExtraServices

const ActivityExtraServicesContent = styled(ExtraServicesContent)`
    .wte-service-options-item{
        .wte-counter-wrap{
            .wte-counter-btn{
                width: 25px;
                height: 25px;
                flex: 0 0 25px;
                font-size: 12px;
            }
            .wte-counter-input{
                width: 30px;
            }
        }

        .wte-service-option-quantity-wrap{
            gap: 0;
        }
        .wpte-tag{
            font-size: 11px;
            padding: 3px 6px;
        }
    }
`

const ActivityServiceOptionBox = styled(ServiceOptionsBox)`
    .wte-service-multiple-options-header{
        padding: 10px 16px;
    }
    .wte-service-options-title{
        font-size: 14px;
    }
    .wte-service-multiple-options-body{
        padding: 0;
        > * , > .wte-service-options-item{
            padding: 12px 16px;
            border: none;
            border-radius: 0;
            background: none;
            .wte-service-option-description {
                -webkit-line-clamp: 1;
                &.is-expanded{
                    -webkit-line-clamp: unset;
                }
            }
        }
    }
`
